import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Divider,
  InputAdornment,
  Tooltip
} from '@mui/material';
import {
  Security as SecurityIcon,
  Phone as PhoneIcon,
  QrCode as QrCodeIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { useLocale } from '../../context/LocaleContext';
import {
  getEnrolledFactors,
  isMfaEnabled,
  reauthenticateUser,
  startSmsEnrollment,
  completeSmsEnrollment,
  generateTotpSecret,
  completeTotpEnrollment,
  unenrollFactor,
  FACTOR_IDS,
  clearRecaptchaVerifier
} from '../../services/mfaService';

/**
 * Security Settings Component
 * Allows users to manage their 2FA settings
 */
const SecuritySettings = () => {
  const { t } = useLocale();
  
  // State
  const [enrolledFactors, setEnrolledFactors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Dialogs
  const [smsDialogOpen, setSmsDialogOpen] = useState(false);
  const [totpDialogOpen, setTotpDialogOpen] = useState(false);
  const [reauthDialogOpen, setReauthDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [factorToRemove, setFactorToRemove] = useState(null);
  
  // Enrollment state
  const [activeStep, setActiveStep] = useState(0);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [smsVerificationId, setSmsVerificationId] = useState(null);
  const [totpSecret, setTotpSecret] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  
  const recaptchaContainerRef = useRef(null);

  // Load enrolled factors on mount
  useEffect(() => {
    loadEnrolledFactors();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearRecaptchaVerifier();
    };
  }, []);

  const loadEnrolledFactors = () => {
    try {
      const factors = getEnrolledFactors();
      setEnrolledFactors(factors);
    } catch (err) {
      console.error('Error loading enrolled factors:', err);
    } finally {
      setLoading(false);
    }
  };

  // Reset enrollment state
  const resetEnrollmentState = () => {
    setActiveStep(0);
    setPassword('');
    setPhoneNumber('');
    setVerificationCode('');
    setSmsVerificationId(null);
    setTotpSecret(null);
    setError(null);
    clearRecaptchaVerifier();
  };

  // Handle reauthentication
  const handleReauthenticate = async () => {
    setError(null);
    setLoading(true);
    
    try {
      await reauthenticateUser(password);
      setReauthDialogOpen(false);
      setPassword('');
      
      // Execute pending action
      if (pendingAction === 'sms') {
        setSmsDialogOpen(true);
      } else if (pendingAction === 'totp') {
        setTotpDialogOpen(true);
      } else if (pendingAction === 'remove' && factorToRemove) {
        await performRemoveFactor();
      }
      setPendingAction(null);
    } catch (err) {
      console.error('Reauthentication error:', err);
      if (err.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else {
        setError(err.message || 'Authentication failed');
      }
    } finally {
      setLoading(false);
    }
  };

  // Start SMS enrollment
  const handleStartSmsEnrollment = () => {
    resetEnrollmentState();
    setPendingAction('sms');
    setReauthDialogOpen(true);
  };

  // Handle SMS phone number submission
  const handleSendSmsCode = async () => {
    if (!phoneNumber || phoneNumber.length < 10) {
      setError('Please enter a valid phone number');
      return;
    }
    
    setError(null);
    setLoading(true);
    
    try {
      // Format phone number to E.164
      let formattedPhone = phoneNumber.replace(/\D/g, '');
      if (!formattedPhone.startsWith('+')) {
        // Assume US number if no country code
        if (formattedPhone.length === 10) {
          formattedPhone = '+1' + formattedPhone;
        } else if (!formattedPhone.startsWith('1') && formattedPhone.length === 11) {
          formattedPhone = '+' + formattedPhone;
        } else {
          formattedPhone = '+' + formattedPhone;
        }
      }
      
      const verificationId = await startSmsEnrollment(formattedPhone, 'security-recaptcha-container');
      setSmsVerificationId(verificationId);
      setActiveStep(1);
    } catch (err) {
      console.error('Error sending SMS:', err);
      if (err.code === 'auth/invalid-phone-number') {
        setError('Invalid phone number format. Please use format: +1234567890');
      } else if (err.code === 'auth/requires-recent-login') {
        setError('Please sign out and sign back in, then try again.');
      } else if (err.code === 'auth/argument-error') {
        setError('SMS authentication is not properly configured. Please ensure SMS Multi-factor Authentication is enabled in Firebase Console (Authentication → Sign-in method → SMS Multi-factor Authentication).');
      } else if (err.code === 'auth/operation-not-allowed') {
        setError('SMS authentication is not enabled. Please enable it in Firebase Console.');
      } else {
        setError(err.message || 'Failed to send verification code');
      }
    } finally {
      setLoading(false);
    }
  };

  // Complete SMS enrollment
  const handleVerifySmsCode = async () => {
    if (!verificationCode || verificationCode.length < 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }
    
    setError(null);
    setLoading(true);
    
    try {
      await completeSmsEnrollment(smsVerificationId, verificationCode, 'Phone');
      setSmsDialogOpen(false);
      resetEnrollmentState();
      loadEnrolledFactors();
      setSuccess('SMS two-factor authentication has been enabled successfully.');
    } catch (err) {
      console.error('Error verifying SMS code:', err);
      if (err.code === 'auth/invalid-verification-code') {
        setError('Invalid verification code. Please try again.');
      } else if (err.code === 'auth/code-expired') {
        setError('Verification code has expired. Please request a new one.');
        setActiveStep(0);
      } else {
        setError(err.message || 'Verification failed');
      }
    } finally {
      setLoading(false);
    }
  };

  // Start TOTP enrollment
  const handleStartTotpEnrollment = () => {
    resetEnrollmentState();
    setPendingAction('totp');
    setReauthDialogOpen(true);
  };

  // Generate TOTP secret and show QR code
  const handleGenerateTotpSecret = async () => {
    setError(null);
    setLoading(true);
    
    try {
      const secret = await generateTotpSecret();
      setTotpSecret(secret);
      setActiveStep(1);
    } catch (err) {
      console.error('Error generating TOTP secret:', err);
      if (err.code === 'auth/requires-recent-login') {
        setError('Please sign out and sign back in, then try again.');
      } else {
        setError(err.message || 'Failed to generate authenticator setup');
      }
    } finally {
      setLoading(false);
    }
  };

  // Complete TOTP enrollment
  const handleVerifyTotpCode = async () => {
    if (!verificationCode || verificationCode.length < 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }
    
    setError(null);
    setLoading(true);
    
    try {
      await completeTotpEnrollment(totpSecret.secret, verificationCode, 'Authenticator App');
      setTotpDialogOpen(false);
      resetEnrollmentState();
      loadEnrolledFactors();
      setSuccess('Authenticator app two-factor authentication has been enabled successfully.');
    } catch (err) {
      console.error('Error verifying TOTP code:', err);
      if (err.code === 'auth/invalid-verification-code') {
        setError('Invalid verification code. Please check your authenticator app and try again.');
      } else {
        setError(err.message || 'Verification failed');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle remove factor
  const handleRemoveFactor = (factor) => {
    setFactorToRemove(factor);
    setPendingAction('remove');
    setReauthDialogOpen(true);
  };

  // Perform factor removal
  const performRemoveFactor = async () => {
    if (!factorToRemove) return;
    
    setError(null);
    setLoading(true);
    
    try {
      await unenrollFactor(factorToRemove.uid);
      setRemoveDialogOpen(false);
      setFactorToRemove(null);
      loadEnrolledFactors();
      setSuccess('Two-factor authentication method has been removed.');
    } catch (err) {
      console.error('Error removing factor:', err);
      setError(err.message || 'Failed to remove authentication method');
    } finally {
      setLoading(false);
    }
  };

  // Copy secret key to clipboard
  const handleCopySecretKey = () => {
    if (totpSecret?.secretKey) {
      navigator.clipboard.writeText(totpSecret.secretKey);
      setSuccess('Secret key copied to clipboard');
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  // Check if factor type is already enrolled
  const isPhoneEnrolled = enrolledFactors.some(f => f.factorId === FACTOR_IDS.PHONE);
  const isTotpEnrolled = enrolledFactors.some(f => f.factorId === FACTOR_IDS.TOTP);

  if (loading && enrolledFactors.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
          <CircularProgress />
        </Box>
        {/* Hidden reCAPTCHA container - must always be present */}
        <div id="security-recaptcha-container" style={{ display: 'none' }} />
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <SecurityIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6">
          {t('account.security.title', 'Two-Factor Authentication')}
        </Typography>
        <Chip 
          label={isMfaEnabled() ? 'Enabled' : 'Disabled'}
          color={isMfaEnabled() ? 'success' : 'default'}
          size="small"
          sx={{ ml: 2 }}
        />
      </Box>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('account.security.description', 'Add an extra layer of security to your account by requiring a verification code in addition to your password.')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Enrolled Factors List */}
      {enrolledFactors.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t('account.security.enrolledMethods', 'Enrolled Methods')}
          </Typography>
          <List dense>
            {enrolledFactors.map((factor) => (
              <ListItem
                key={factor.uid}
                sx={{
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  mb: 1
                }}
              >
                <ListItemIcon>
                  {factor.factorId === FACTOR_IDS.PHONE ? (
                    <PhoneIcon color="primary" />
                  ) : (
                    <SecurityIcon color="primary" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={factor.displayName}
                  secondary={
                    factor.factorId === FACTOR_IDS.PHONE
                      ? `Phone: ***-***-${factor.phoneNumber?.slice(-4) || '****'}`
                      : 'Authenticator App'
                  }
                />
                <ListItemSecondaryAction>
                  <Tooltip title="Remove this method">
                    <IconButton
                      edge="end"
                      onClick={() => handleRemoveFactor(factor)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Add Methods Section */}
      <Typography variant="subtitle2" sx={{ mb: 2 }}>
        {t('account.security.addMethod', 'Add Authentication Method')}
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {/* SMS Option */}
        <Button
          variant={isPhoneEnrolled ? 'outlined' : 'contained'}
          startIcon={isPhoneEnrolled ? <CheckIcon /> : <AddIcon />}
          onClick={handleStartSmsEnrollment}
          disabled={isPhoneEnrolled}
          sx={{ minWidth: 200 }}
        >
          <PhoneIcon sx={{ mr: 1 }} />
          {isPhoneEnrolled ? 'SMS Enabled' : 'Add SMS'}
        </Button>

        {/* TOTP Option */}
        <Button
          variant={isTotpEnrolled ? 'outlined' : 'contained'}
          startIcon={isTotpEnrolled ? <CheckIcon /> : <AddIcon />}
          onClick={handleStartTotpEnrollment}
          disabled={isTotpEnrolled}
          sx={{ minWidth: 200 }}
        >
          <QrCodeIcon sx={{ mr: 1 }} />
          {isTotpEnrolled ? 'Authenticator Enabled' : 'Add Authenticator App'}
        </Button>
      </Box>

      {/* Hidden reCAPTCHA container */}
      <div id="security-recaptcha-container" ref={recaptchaContainerRef} />

      {/* Reauthentication Dialog */}
      <Dialog open={reauthDialogOpen} onClose={() => setReauthDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Your Password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            For security, please enter your password to continue.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setReauthDialogOpen(false); setError(null); setPassword(''); }}>
            Cancel
          </Button>
          <Button 
            onClick={handleReauthenticate} 
            variant="contained"
            disabled={!password || loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* SMS Enrollment Dialog */}
      <Dialog open={smsDialogOpen} onClose={() => { setSmsDialogOpen(false); resetEnrollmentState(); }} maxWidth="sm" fullWidth>
        <DialogTitle>Set Up SMS Authentication</DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} sx={{ mb: 3, mt: 1 }}>
            <Step>
              <StepLabel>Enter Phone Number</StepLabel>
            </Step>
            <Step>
              <StepLabel>Verify Code</StepLabel>
            </Step>
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {activeStep === 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter your phone number. We'll send you a verification code via SMS.
              </Typography>
              <TextField
                fullWidth
                label="Phone Number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="+1 (555) 123-4567"
                helperText="Include country code (e.g., +1 for US)"
                autoFocus
              />
            </Box>
          )}

          {activeStep === 1 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter the 6-digit code sent to your phone.
              </Typography>
              <TextField
                fullWidth
                label="Verification Code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                inputProps={{ 
                  maxLength: 6,
                  style: { textAlign: 'center', letterSpacing: '0.5em', fontSize: '1.25rem' }
                }}
                autoFocus
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSmsDialogOpen(false); resetEnrollmentState(); }}>
            Cancel
          </Button>
          {activeStep === 0 && (
            <Button 
              onClick={handleSendSmsCode} 
              variant="contained"
              disabled={!phoneNumber || loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Send Code'}
            </Button>
          )}
          {activeStep === 1 && (
            <Button 
              onClick={handleVerifySmsCode} 
              variant="contained"
              disabled={verificationCode.length < 6 || loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Verify & Enable'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* TOTP Enrollment Dialog */}
      <Dialog open={totpDialogOpen} onClose={() => { setTotpDialogOpen(false); resetEnrollmentState(); }} maxWidth="sm" fullWidth>
        <DialogTitle>Set Up Authenticator App</DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} sx={{ mb: 3, mt: 1 }}>
            <Step>
              <StepLabel>Scan QR Code</StepLabel>
            </Step>
            <Step>
              <StepLabel>Verify Code</StepLabel>
            </Step>
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {activeStep === 0 && !totpSecret && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Use an authenticator app like Google Authenticator, Microsoft Authenticator, or Authy.
              </Typography>
              <Button 
                onClick={handleGenerateTotpSecret} 
                variant="contained"
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Generate QR Code'}
              </Button>
            </Box>
          )}

          {activeStep === 1 && totpSecret && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                Scan this QR code with your authenticator app:
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Paper elevation={2} sx={{ p: 2, bgcolor: 'white' }}>
                  <QRCodeSVG 
                    value={totpSecret.qrCodeUrl} 
                    size={200}
                    level="M"
                  />
                </Paper>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textAlign: 'center' }}>
                Or manually enter this key:
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 3 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontFamily: 'monospace', 
                    bgcolor: 'grey.100', 
                    px: 2, 
                    py: 1, 
                    borderRadius: 1,
                    wordBreak: 'break-all'
                  }}
                >
                  {totpSecret.secretKey}
                </Typography>
                <Tooltip title="Copy to clipboard">
                  <IconButton onClick={handleCopySecretKey} size="small">
                    <CopyIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter the 6-digit code from your authenticator app:
              </Typography>
              <TextField
                fullWidth
                label="Verification Code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                inputProps={{ 
                  maxLength: 6,
                  style: { textAlign: 'center', letterSpacing: '0.5em', fontSize: '1.25rem' }
                }}
                autoFocus
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setTotpDialogOpen(false); resetEnrollmentState(); }}>
            Cancel
          </Button>
          {activeStep === 1 && totpSecret && (
            <Button 
              onClick={handleVerifyTotpCode} 
              variant="contained"
              disabled={verificationCode.length < 6 || loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Verify & Enable'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Remove Factor Confirmation Dialog */}
      <Dialog open={removeDialogOpen} onClose={() => setRemoveDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove Authentication Method?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to remove "{factorToRemove?.displayName}"? 
            {enrolledFactors.length === 1 && (
              <strong> This will disable two-factor authentication on your account.</strong>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveDialogOpen(false)}>Cancel</Button>
          <Button onClick={performRemoveFactor} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default SecuritySettings;

